import { useAuthStore } from '@/features/auth/auth.store';
import { Logo } from '@/components/Logo';

/**
 * Exibida quando um usuário se autenticou com sucesso pelo Azure SSO, mas ainda
 * não tem acesso liberado no sistema.
 *
 * Isso acontece em dois casos:
 * - Primeiro login: a conta foi criada automaticamente com status PENDING_APPROVAL.
 * - Conta suspensa ou desativada: o administrador bloqueou o acesso.
 *
 * O usuário não tem sessão — esta tela é pré-autenticação.
 */
export function PendingApprovalPage() {
  const { user } = useAuthStore();

  return (
    <div className="grid min-h-full md:grid-cols-[5fr_7fr]">
      {/* Painel de marca */}
      <div className="hidden md:flex flex-col justify-between bg-sidebar px-10 py-12">
        <Logo />
        <div>
          <div className="mb-2 h-[3px] w-10 rounded-full gradient-primary" />
          <p className="mt-6 font-heading text-2xl font-bold leading-snug text-white/90">
            Service Desk e<br />Gestão de Projetos
          </p>
          <p className="mt-3 text-sm text-sidebar-foreground/55">
            Plataforma interna · ITIL 4
          </p>
        </div>
        <p className="text-xs text-sidebar-foreground/35">Aptum Engenharia</p>
      </div>

      {/* Painel de conteúdo */}
      <div className="grid place-items-center bg-background px-6 py-14">
        <div className="w-full max-w-sm animate-slide-up text-center">
          <div className="mb-8 flex justify-center md:hidden">
            <Logo />
          </div>

          {/* Ícone de relógio / espera */}
          <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-amber-500/10 ring-1 ring-amber-500/20">
            <HourglassIcon />
          </div>

          <h1 className="text-2xl font-bold tracking-tight">Aguardando acesso</h1>

          {user?.email ? (
            <p className="mt-2 text-sm text-muted-foreground">
              A conta{' '}
              <span className="font-medium text-foreground">{user.email}</span>{' '}
              foi identificada, mas ainda não tem perfil atribuído neste sistema.
            </p>
          ) : (
            <p className="mt-2 text-sm text-muted-foreground">
              Sua conta Microsoft foi identificada, mas ainda não tem perfil atribuído neste sistema.
            </p>
          )}

          <div className="mt-6 rounded-lg border border-border/60 bg-muted/30 px-5 py-4 text-left">
            <p className="text-sm font-medium">O que acontece agora?</p>
            <ul className="mt-2 space-y-1.5 text-sm text-muted-foreground">
              <li className="flex gap-2">
                <span className="mt-0.5 shrink-0 text-amber-500">①</span>
                Um administrador receberá uma notificação sobre o seu acesso pendente.
              </li>
              <li className="flex gap-2">
                <span className="mt-0.5 shrink-0 text-amber-500">②</span>
                Após a atribuição do perfil, você já poderá entrar normalmente.
              </li>
              <li className="flex gap-2">
                <span className="mt-0.5 shrink-0 text-amber-500">③</span>
                Se urgente, entre em contato com a equipe de TI da Aptum.
              </li>
            </ul>
          </div>

          <a
            href="/api/v1/auth/azure/login"
            className={
              'mt-6 flex w-full items-center justify-center gap-3 rounded-lg border border-input/80 ' +
              'bg-muted/40 px-4 py-2.5 text-sm font-medium transition-colors ' +
              'hover:bg-muted hover:border-input focus-visible:outline-none'
            }
          >
            <MicrosoftIcon />
            Tentar novamente com Microsoft
          </a>
        </div>
      </div>
    </div>
  );
}

function HourglassIcon() {
  return (
    <svg
      width="36"
      height="36"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-amber-500"
      aria-hidden
    >
      <path d="M5 22h14" />
      <path d="M5 2h14" />
      <path d="M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22" />
      <path d="M7 2v4.172a2 2 0 0 0 .586 1.414L12 12l4.414-4.414A2 2 0 0 0 17 6.172V2" />
    </svg>
  );
}

function MicrosoftIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 21 21" aria-hidden>
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}
